import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ExpandMore from "@mui/icons-material/ExpandMore";

export default function AboutSectionNavigation({ sections }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [readingProgress, setReadingProgress] = useState(0);
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    let frame = null;
    let mounted = true;
    const page = document.querySelector(".brand-about");
    const content = document.querySelector(".brand-about-content");
    const targets = sections.map(([id]) => document.getElementById(id));
    const update = () => {
      frame = null;
      if (!mounted) return;
      const offset = (page && parseFloat(getComputedStyle(page).getPropertyValue("--about-scroll-offset"))) || 122;
      const positions = targets.map(target => target?.getBoundingClientRect());
      if (!positions.some(position => position?.height > 0)) return;
      let current = 0;
      positions.forEach((position, index) => {
        if (position && position.top <= offset + 2) current = index;
      });
      setActiveIndex(current);
      const position = positions[current];
      const withinSection = position ? Math.max(0, Math.min(1, (offset - position.top) / Math.max(1, position.height))) : 0;
      setReadingProgress(Math.round(((current + withinSection) / sections.length) * 1000) / 1000);
    };
    const schedule = () => {
      if (mounted && frame === null) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.addEventListener("hashchange", schedule);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    if (content) observer?.observe(content);
    document.fonts?.ready.then(schedule);
    update();
    return () => {
      mounted = false;
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("hashchange", schedule);
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [sections]);

  const next = sections[activeIndex + 1];
  const destination = next ? "#" + next[0] : "#about-contact";
  const nextLabel = next ? next[1] : "Talk to us";
  const nextLink = () => (
    <a className="brand-about-next-section" href={destination} aria-label={next ? "Next section: " + nextLabel : "Continue to Talk to us"}>
      <span>
        <span className="brand-about-nav-caption">{next ? "Up next" : "Continue the conversation"}</span>
        <span className="brand-about-next-label">{nextLabel}</span>
      </span>
      <ArrowForward fontSize="small" aria-hidden="true" />
    </a>
  );

  return (
    <aside className="brand-about-navigation" style={{ "--about-ruler-progress": readingProgress }}>
      <div className="brand-about-desktop-navigation">
        <div className="brand-about-nav-heading"><span>On this page</span><span>{String(activeIndex + 1).padStart(2, "0")} / {String(sections.length).padStart(2, "0")}</span></div>
        <nav aria-label="On this page">
          <ol className="brand-about-nav-list">
            <li className="brand-about-nav-marker" aria-hidden="true" style={{ transform: `translateY(${activeIndex * 44}px)` }} />
            {sections.map(([id, label], index) => (
              <li key={id}>
                <a href={"#" + id} aria-current={activeIndex === index ? "location" : undefined}>
                  <span className="brand-about-nav-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                  <span>{label}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
        {nextLink()}
      </div>
      <div className="brand-about-mobile-navigation">
        <div className="brand-about-select-wrap">
          <label className="brand-about-nav-caption" htmlFor="about-section-select">Explore this page</label>
          <div className="brand-about-select-control">
            <select id="about-section-select" value={sections[activeIndex][0]} onChange={event => {
              const id = event.target.value;
              const destinationHash = "#" + id;
              if (hash === destinationHash) document.getElementById(id)?.scrollIntoView();
              else navigate({ hash: destinationHash });
            }} aria-label="Jump to section">
              {sections.map(([id, label], index) => <option key={id} value={id}>{String(index + 1).padStart(2, "0")} · {label}</option>)}
            </select>
            <ExpandMore fontSize="small" aria-hidden="true" />
          </div>
        </div>
        {nextLink()}
      </div>
    </aside>
  );
}
